import React, { useEffect, type ReactNode } from 'react';
import type { Action } from 'react-sweet-state';
import { createStore, createStateHook, createActionsHook } from 'react-sweet-state';

import { abortAll } from '@atlaskit/react-ufo/interaction-metrics';

import { fg } from '@confluence/feature-gating';

type State = {
	active: boolean;
	nav4GlobalChangeboardingActive: boolean;
	nav4SpaceSettingsChangeboardingActive: boolean;
};

const initialState = {
	active: false,
	nav4GlobalChangeboardingActive: false,
	nav4SpaceSettingsChangeboardingActive: false,
};

const actions = {
	setActive:
		(active: boolean): Action<State> =>
		({ setState }) => {
			setState({ active });
		},
	nav4GlobalChangeboardingActive:
		(nav4GlobalChangeboardingActive: boolean): Action<State> =>
		({ setState }) => {
			setState({ nav4GlobalChangeboardingActive });
		},
	nav4SpaceSettingsChangeboardingActive:
		(nav4SpaceSettingsChangeboardingActive: boolean): Action<State> =>
		({ setState }) => {
			setState({ nav4SpaceSettingsChangeboardingActive });
		},
};

const ModernizeChangeboardingStore = createStore({
	initialState,
	actions,
});

export const useModernizeChangeboardingActive = createStateHook(ModernizeChangeboardingStore, {
	selector: (state) => state.active,
});

export const useModernizeChangeboardingState = createStateHook(ModernizeChangeboardingStore);

const useActions = createActionsHook(ModernizeChangeboardingStore);

const WithModernizeChangeboardingActiveComponent = ({
	children,
	setActive,
}: {
	children: ReactNode;
	setActive: (active: boolean) => void | Promise<void>;
}) => {
	useEffect(() => {
		if (fg('confluence_frontend_exclude_changeboarding_from_vc'))
			abortAll('new_interaction', 'modernize-changeboarding');
		void setActive(true);
		return () => {
			void setActive(false);
		};
	}, [setActive]);

	return children;
};

export const WithModernizeChangeboardingActive = ({ children }: { children: ReactNode }) => {
	const { setActive } = useActions();
	return (
		<WithModernizeChangeboardingActiveComponent setActive={setActive}>
			{children}
		</WithModernizeChangeboardingActiveComponent>
	);
};

export const WithModernizeNav4GlobalChangeboardingActive = ({
	children,
}: {
	children: ReactNode;
}) => {
	const { nav4GlobalChangeboardingActive } = useActions();
	return (
		<WithModernizeChangeboardingActiveComponent setActive={nav4GlobalChangeboardingActive}>
			{children}
		</WithModernizeChangeboardingActiveComponent>
	);
};

export const WithModernizeNav4SpaceSettingsChangeboardingActive = ({
	children,
}: {
	children: ReactNode;
}) => {
	const { nav4SpaceSettingsChangeboardingActive } = useActions();
	return (
		<WithModernizeChangeboardingActiveComponent setActive={nav4SpaceSettingsChangeboardingActive}>
			{children}
		</WithModernizeChangeboardingActiveComponent>
	);
};
